import { useState, useEffect, useMemo, useCallback } from 'react';
import { StockEntity, StockCategoryEntity, StockPlaceEntity } from '../types/data';
import { stocksData, stockCategoriesData, stockPlacesData } from '../data/stockData';
import { stockRepo } from '../utils/repos/stockRepo';

const STOCKS_STORAGE_KEY = 'stocks';
const STOCK_CATEGORIES_STORAGE_KEY = 'stock_categories';
const STOCK_PLACES_STORAGE_KEY = 'stock_places';
const BACKEND = (process.env.REACT_APP_BACKEND || 'local').toLowerCase();

export const useStocks = () => {
  // Data state
  const [stocks, setStocks] = useState<StockEntity[]>(() => {
    if (BACKEND !== 'supabase') {
      try {
        const storedStocks = localStorage.getItem(STOCKS_STORAGE_KEY);
        if (storedStocks) return JSON.parse(storedStocks);
      } catch (error) {
        console.error('Failed to load stocks from localStorage:', error);
      }
      return stocksData;
    }
    // supabase: start empty; fetch in effect
    return [];
  });

  const [stockCategories, setStockCategories] = useState<StockCategoryEntity[]>(() => {
    if (BACKEND !== 'supabase') {
      try {
        const storedCategories = localStorage.getItem(STOCK_CATEGORIES_STORAGE_KEY);
        if (storedCategories) return JSON.parse(storedCategories);
      } catch (error) {
        console.error('Failed to load stock categories from localStorage:', error);
      }
      return stockCategoriesData;
    }
    return [];
  });

  const [stockPlaces, setStockPlaces] = useState<StockPlaceEntity[]>(() => {
    if (BACKEND !== 'supabase') {
      try {
        const storedPlaces = localStorage.getItem(STOCK_PLACES_STORAGE_KEY);
        if (storedPlaces) return JSON.parse(storedPlaces);
      } catch (error) {
        console.error('Failed to load stock places from localStorage:', error);
      }
      return stockPlacesData;
    }
    return [];
  });

  // Fetch from Supabase on mount when enabled
  useEffect(() => {
    if (BACKEND !== 'supabase') return;
    let alive = true;
    (async () => {
      try {
        const [loadedStocks, loadedCategories, loadedPlaces] = await Promise.all([
          stockRepo.listStocks(),
          stockRepo.listCategories(),
          stockRepo.listPlaces(),
        ]);
        if (!alive) return;
        setStocks(loadedStocks);
        setStockCategories(loadedCategories);
        setStockPlaces(loadedPlaces);
      } catch (e) {
        console.error('Failed to load stocks from Supabase:', e);
      }
    })();
    return () => { alive = false; };
  }, []);

  // アクティブな（削除されていない）ストックを取得（表示順で安定化）
  const activeStocks = useMemo(() => {
    return stocks
      .filter(stock => !stock.is_deleted)
      .slice()
      .sort((a, b) => {
        const ac = a.stock_category_id ?? '';
        const bc = b.stock_category_id ?? '';
        if (ac !== bc) return ac.localeCompare(bc);
        const ao = a.sort_order ?? 0;
        const bo = b.sort_order ?? 0;
        if (ao !== bo) return ao - bo;
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      });
  }, [stocks]);
  
  // アクティブなストックカテゴリを取得（表示順で安定化）
  const activeStockCategories = useMemo(() => {
    return stockCategories
      .filter(category => !category.is_deleted)
      .slice()
      .sort((a, b) => {
        const ao = a.sort_order ?? 0;
        const bo = b.sort_order ?? 0;
        if (ao !== bo) return ao - bo;
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      });
  }, [stockCategories]);

  const activeStockPlaces = useMemo(() => {
    return stockPlaces
      .filter(place => !place.is_deleted)
      .slice()
      .sort((a, b) => {
        const ao = a.sort_order ?? 0;
        const bo = b.sort_order ?? 0;
        if (ao !== bo) return ao - bo;
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      });
  }, [stockPlaces]);

  // Persist to localStorage only in local mode
  useEffect(() => {
    if (BACKEND === 'supabase') return;
    try {
      localStorage.setItem(STOCKS_STORAGE_KEY, JSON.stringify(stocks));
    } catch (error) {
      console.error('Failed to save stocks to localStorage:', error);
    }
  }, [stocks]);

  useEffect(() => {
    if (BACKEND === 'supabase') return;
    try {
      localStorage.setItem(STOCK_CATEGORIES_STORAGE_KEY, JSON.stringify(stockCategories));
    } catch (error) {
      console.error('Failed to save stock categories to localStorage:', error);
    }
  }, [stockCategories]);

  useEffect(() => {
    if (BACKEND === 'supabase') return;
    try {
      localStorage.setItem(STOCK_PLACES_STORAGE_KEY, JSON.stringify(stockPlaces));
    } catch (error) {
      console.error('Failed to save stock places to localStorage:', error);
    }
  }, [stockPlaces]);


  const createStock = useCallback(async (stockData: Omit<StockEntity, 'id' | 'created_at' | 'modified_at' | 'is_deleted'>): Promise<StockEntity> => {
    const now = new Date().toISOString();
    const nextSortOrder = Math.max(
      0,
      ...stocks
        .filter(s => !s.is_deleted && s.stock_category_id === stockData.stock_category_id)
        .map(s => s.sort_order ?? 0)
    ) + 1000;
    if (BACKEND === 'supabase') {
      try {
        const saved = await stockRepo.createStock({
          ...stockData,
          sort_order: nextSortOrder,
          created_at: now,
          modified_at: now,
          is_deleted: false,
        } as any);
        setStocks(prev => [...prev, saved]);
        return saved;
      } catch (e) {
        console.error('Failed to create stock on Supabase:', e);
        throw e;
      }
    }
    const newStock: StockEntity = {
      id: (typeof crypto !== 'undefined' && 'randomUUID' in crypto) ? crypto.randomUUID() : `stock-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      created_at: now,
      modified_at: now,
      is_deleted: false,
      sort_order: nextSortOrder,
      ...stockData,
    };
    setStocks(prev => [...prev, newStock]);
    return newStock;
  }, [stocks]);

  const updateStock = useCallback((stockId: string, patch: Partial<StockEntity>) => {
    const updatedAt = new Date().toISOString();
    setStocks(prev => prev.map(s => s.id === stockId ? { ...s, ...patch, modified_at: updatedAt } : s));
    if (BACKEND === 'supabase') {
      stockRepo.updateStock(stockId, patch).catch((e) => {
        console.error('Failed to update stock on Supabase:', e);
      });
    }
  }, []);

  const deleteStock = useCallback((stockId: string) => {
    const updatedAt = new Date().toISOString();
    setStocks(prev => prev.map(s => s.id === stockId ? { ...s, is_deleted: true, modified_at: updatedAt } : s));
    if (BACKEND === 'supabase') {
      stockRepo.softDeleteStock(stockId).catch((e) => {
        console.error('Failed to delete stock on Supabase:', e);
      });
    }
  }, []);

  const createStockCategory = useCallback(async (categoryData: Omit<StockCategoryEntity, 'id' | 'created_at' | 'modified_at' | 'is_deleted'>): Promise<StockCategoryEntity> => {
    const now = new Date().toISOString();
    const nextSortOrder = Math.max(
      0,
      ...stockCategories
        .filter(c => !c.is_deleted)
        .map(c => c.sort_order ?? 0)
    ) + 1000;
    if (BACKEND === 'supabase') {
      try {
        const saved = await stockRepo.createCategory({
          ...categoryData,
          sort_order: nextSortOrder,
          created_at: now,
          modified_at: now,
          is_deleted: false,
        } as any);
        setStockCategories(prev => [...prev, saved]);
        return saved;
      } catch (e) {
        console.error('Failed to create category on Supabase:', e);
        throw e;
      }
    }
    const newCategory: StockCategoryEntity = {
      id: (typeof crypto !== 'undefined' && 'randomUUID' in crypto) ? crypto.randomUUID() : `stock-cat-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      created_at: now,
      modified_at: now,
      is_deleted: false,
      sort_order: nextSortOrder,
      ...categoryData,
    };
    setStockCategories(prev => [...prev, newCategory]);
    return newCategory;
  }, [stockCategories]);

  const createStockPlace = useCallback(async (placeData: Omit<StockPlaceEntity, 'id' | 'created_at' | 'modified_at' | 'is_deleted'>): Promise<StockPlaceEntity> => {
    const now = new Date().toISOString();
    const nextSortOrder = Math.max(
      0,
      ...stockPlaces
        .filter(p => !p.is_deleted)
        .map(p => p.sort_order ?? 0)
    ) + 1000;
    if (BACKEND === 'supabase') {
      try {
        const saved = await stockRepo.createPlace({
          ...placeData,
          sort_order: nextSortOrder,
          created_at: now,
          modified_at: now,
          is_deleted: false,
        } as any);
        setStockPlaces(prev => [...prev, saved]);
        return saved;
      } catch (e) {
        console.error('Failed to create stock place on Supabase:', e);
        throw e;
      }
    }
    const newPlace: StockPlaceEntity = {
      id: (typeof crypto !== 'undefined' && 'randomUUID' in crypto) ? crypto.randomUUID() : `stock-place-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      created_at: now,
      modified_at: now,
      is_deleted: false,
      sort_order: nextSortOrder,
      ...placeData,
    };
    setStockPlaces(prev => [...prev, newPlace]);
    return newPlace;
  }, [stockPlaces]);

  const reorderStockCategories = useCallback(async (sourceId: string, targetId: string, insertAfter: boolean) => {
    const ordered = [...stockCategories]
      .filter(c => !c.is_deleted)
      .sort((a, b) => {
        const ao = a.sort_order ?? 0;
        const bo = b.sort_order ?? 0;
        if (ao !== bo) return ao - bo;
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      });
    const sourceIndex = ordered.findIndex(c => c.id === sourceId);
    const targetIndex = ordered.findIndex(c => c.id === targetId);
    if (sourceIndex < 0 || targetIndex < 0) return;
    if (sourceIndex === targetIndex) return;

    const next = [...ordered];
    const [moved] = next.splice(sourceIndex, 1);
    const insertionIndex = (() => {
      const adjustedTargetIndex = sourceIndex < targetIndex ? targetIndex - 1 : targetIndex;
      return insertAfter ? adjustedTargetIndex + 1 : adjustedTargetIndex;
    })();
    next.splice(insertionIndex, 0, moved);

    const prevNeighbor = insertionIndex > 0 ? next[insertionIndex - 1] : undefined;
    const nextNeighbor = insertionIndex < next.length - 1 ? next[insertionIndex + 1] : undefined;
    const prevOrder = prevNeighbor?.sort_order ?? 0;
    const nextOrder = nextNeighbor?.sort_order;

    const updatedAt = new Date().toISOString();
    const shouldRenumber = (() => {
      if (nextNeighbor && nextOrder !== undefined && nextOrder - prevOrder <= 1) return true;
      if (!prevNeighbor && nextNeighbor && nextOrder !== undefined && nextOrder <= 1000) return true;
      return false;
    })();

    if (shouldRenumber) {
      const renumbered = next.map((c, i) => ({
        ...c,
        sort_order: (i + 1) * 1000,
        modified_at: updatedAt,
      }));
      setStockCategories(prev => prev.map(c => {
        const r = renumbered.find(x => x.id === c.id);
        return r ? { ...c, sort_order: r.sort_order, modified_at: r.modified_at } : c;
      }));
      if (BACKEND === 'supabase') {
        await Promise.all(renumbered.map(c => stockRepo.updateCategory(c.id, { sort_order: c.sort_order })));
      }
      return;
    }

    const computedSortOrder = (() => {
      if (!prevNeighbor) {
        if (!nextNeighbor || nextOrder === undefined) return 1000;
        return nextOrder - 1000;
      }
      if (!nextNeighbor || nextOrder === undefined) return prevOrder + 1000;
      return Math.floor(prevOrder + (nextOrder - prevOrder) / 2);
    })();

    setStockCategories(prev => prev.map(c => c.id === moved.id
      ? { ...c, sort_order: computedSortOrder, modified_at: updatedAt }
      : c
    ));
    if (BACKEND === 'supabase') {
      await stockRepo.updateCategory(moved.id, { sort_order: computedSortOrder });
    }
  }, [stockCategories]);

  const reorderStockPlaces = useCallback(async (sourceId: string, targetId: string, insertAfter: boolean) => {
    const ordered = [...stockPlaces]
      .filter(p => !p.is_deleted)
      .sort((a, b) => {
        const ao = a.sort_order ?? 0;
        const bo = b.sort_order ?? 0;
        if (ao !== bo) return ao - bo;
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      });
    const sourceIndex = ordered.findIndex(p => p.id === sourceId);
    const targetIndex = ordered.findIndex(p => p.id === targetId);
    if (sourceIndex < 0 || targetIndex < 0) return;
    if (sourceIndex === targetIndex) return;

    const next = [...ordered];
    const [moved] = next.splice(sourceIndex, 1);
    const insertionIndex = (() => {
      const adjustedTargetIndex = sourceIndex < targetIndex ? targetIndex - 1 : targetIndex;
      return insertAfter ? adjustedTargetIndex + 1 : adjustedTargetIndex;
    })();
    next.splice(insertionIndex, 0, moved);

    const prevNeighbor = insertionIndex > 0 ? next[insertionIndex - 1] : undefined;
    const nextNeighbor = insertionIndex < next.length - 1 ? next[insertionIndex + 1] : undefined;
    const prevOrder = prevNeighbor?.sort_order ?? 0;
    const nextOrder = nextNeighbor?.sort_order;

    const updatedAt = new Date().toISOString();
    const shouldRenumber = (() => {
      if (nextNeighbor && nextOrder !== undefined && nextOrder - prevOrder <= 1) return true;
      if (!prevNeighbor && nextNeighbor && nextOrder !== undefined && nextOrder <= 1000) return true;
      return false;
    })();

    if (shouldRenumber) {
      const renumbered = next.map((p, i) => ({
        ...p,
        sort_order: (i + 1) * 1000,
        modified_at: updatedAt,
      }));
      setStockPlaces(prev => prev.map(p => {
        const r = renumbered.find(x => x.id === p.id);
        return r ? { ...p, sort_order: r.sort_order, modified_at: r.modified_at } : p;
      }));
      if (BACKEND === 'supabase') {
        await Promise.all(renumbered.map(p => stockRepo.updatePlace(p.id, { sort_order: p.sort_order })));
      }
      return;
    }

    const computedSortOrder = (() => {
      if (!prevNeighbor) {
        if (!nextNeighbor || nextOrder === undefined) return 1000;
        return nextOrder - 1000;
      }
      if (!nextNeighbor || nextOrder === undefined) return prevOrder + 1000;
      return Math.floor(prevOrder + (nextOrder - prevOrder) / 2);
    })();

    setStockPlaces(prev => prev.map(p => p.id === moved.id
      ? { ...p, sort_order: computedSortOrder, modified_at: updatedAt }
      : p
    ));
    if (BACKEND === 'supabase') {
      await stockRepo.updatePlace(moved.id, { sort_order: computedSortOrder });
    }
  }, [stockPlaces]);

  const reorderStocks = useCallback(async (
    sourceId: string,
    targetId: string,
    insertAfter: boolean,
    groupBy: 'category' | 'place',
    groupId: string | null
  ) => {
    const matchesGroup = (s: StockEntity) => {
      if (groupBy === 'place') {
        if (groupId === null) return !s.stock_place_id;
        return s.stock_place_id === groupId;
      }
      if (groupId === null) return !s.stock_category_id;
      return s.stock_category_id === groupId;
    };
    const ordered = [...stocks]
      .filter(s => !s.is_deleted && matchesGroup(s))
      .sort((a, b) => {
        const ao = a.sort_order ?? 0;
        const bo = b.sort_order ?? 0;
        if (ao !== bo) return ao - bo;
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      });
    const sourceIndex = ordered.findIndex(s => s.id === sourceId);
    const targetIndex = ordered.findIndex(s => s.id === targetId);
    if (sourceIndex < 0 || targetIndex < 0) return;
    if (sourceIndex === targetIndex) return;

    const next = [...ordered];
    const [moved] = next.splice(sourceIndex, 1);
    const insertionIndex = (() => {
      const adjustedTargetIndex = sourceIndex < targetIndex ? targetIndex - 1 : targetIndex;
      return insertAfter ? adjustedTargetIndex + 1 : adjustedTargetIndex;
    })();
    next.splice(insertionIndex, 0, moved);

    const prevNeighbor = insertionIndex > 0 ? next[insertionIndex - 1] : undefined;
    const nextNeighbor = insertionIndex < next.length - 1 ? next[insertionIndex + 1] : undefined;
    const prevOrder = prevNeighbor?.sort_order ?? 0;
    const nextOrder = nextNeighbor?.sort_order;

    const updatedAt = new Date().toISOString();
    const shouldRenumber = (() => {
      if (nextNeighbor && nextOrder !== undefined && nextOrder - prevOrder <= 1) return true;
      if (!prevNeighbor && nextNeighbor && nextOrder !== undefined && nextOrder <= 1000) return true;
      return false;
    })();

    if (shouldRenumber) {
      const renumbered = next.map((s, i) => ({
        id: s.id,
        sort_order: (i + 1) * 1000,
      }));
      setStocks(prev => prev.map(s => {
        const r = renumbered.find(x => x.id === s.id);
        return r ? { ...s, sort_order: r.sort_order, modified_at: updatedAt } : s;
      }));
      if (BACKEND === 'supabase') {
        await Promise.all(renumbered.map(s => stockRepo.updateStock(s.id, { sort_order: s.sort_order })));
      }
      return;
    }

    const computedSortOrder = (() => {
      if (!prevNeighbor) {
        if (!nextNeighbor || nextOrder === undefined) return 1000;
        return nextOrder - 1000;
      }
      if (!nextNeighbor || nextOrder === undefined) return prevOrder + 1000;
      return Math.floor(prevOrder + (nextOrder - prevOrder) / 2);
    })();

    setStocks(prev => prev.map(s => s.id === moved.id
      ? { ...s, sort_order: computedSortOrder, modified_at: updatedAt }
      : s
    ));
    if (BACKEND === 'supabase') {
      await stockRepo.updateStock(moved.id, { sort_order: computedSortOrder });
    }
  }, [stocks]);

  return {
    stocks,
    activeStocks,
    stockCategories,
    activeStockCategories,
    stockPlaces,
    activeStockPlaces,
    createStock,
    updateStock,
    deleteStock,
    createStockCategory,
    createStockPlace,
    reorderStockCategories,
    reorderStockPlaces,
    reorderStocks,
  };
};
